import { useEffect, useState } from "react";

import { useListSearchState } from "common/hooks";
import { Feature } from "common/types";

import { useBreakpoints, useSearchContext } from "client/common/hooks";
import Page from "client/common/components/layouts/Page";
import Container from "client/common/components/layouts/Container";
import Header from "client/common/components/header/Header";
import { useFeatures } from "client/clinics/hooks";
import { Paths } from "client/routes";

import ProductSearch from "./ProductSearch";
import BrowseCatalogV1 from "./BrowseCatalogV1";
import BrowseCatalogV2 from "./BrowseCatalogV2";
import ProductSearchMobile from "./ProductSearchMobile";
import {
  PRODUCT_LIST_PAGE_SIZE,
  PRODUCT_LIST_PAGE_SIZE_WITH_PANEL,
} from "../constants/filterDefinitions";

import styles from "../styles/ProductSearchPage.module.less";

const initialProductFilters = {
  product_ids: [],
  product_line_ids: [],
  category_ids: [],
  manufacturer_ids: [],
  species: [],
  incentive_ids: [],
  rebate_ids: [],
};

const initialSearch = {
  ...initialProductFilters,
  q: "",
  page: 1,
  page_size: PRODUCT_LIST_PAGE_SIZE,
  sort: "",
};

const initialSearchWithPanel = {
  ...initialSearch,
  page_size: PRODUCT_LIST_PAGE_SIZE_WITH_PANEL,
};

const ProductSearchPage = () => {
  const { shouldFocus, setShouldFocus } = useSearchContext();
  const features = useFeatures();
  const defaultSearch = features[Feature.StorefrontFiltersV2]
    ? initialSearchWithPanel
    : initialSearch;
  const [search, updateSearch] = useListSearchState<Paths["products"]["search"]>(defaultSearch);

  // Show search results if we have any search criteria
  const showSearchResults = !!(
    search.q ||
    search.product_ids?.length ||
    search.product_line_ids?.length ||
    search.category_ids?.length ||
    search.manufacturer_ids?.length ||
    search.species?.length ||
    search.incentive_ids?.length ||
    search.rebate_ids?.length
  );

  // The criteria for determining if the mobile search should be visible
  const shouldOpenMobileSearch = shouldFocus || showSearchResults;

  // Hold this in separate state since autofocus will only be 1 initially and then switch to 0
  // so that refreshing the page or sending a link will not focus on the searchbars
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(shouldOpenMobileSearch);

  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const BrowseCatalog = features[Feature.CarouselV2] ? BrowseCatalogV2 : BrowseCatalogV1;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  // We reset the product and product line IDs on change to the search query
  // because there's no UI that indicates that these filters are applied
  const handleSearch = (q: string) => updateSearch({ q, product_ids: [], product_line_ids: [] });

  const handleClose = () => {
    setIsMobileSearchVisible(false);
    setShouldFocus(false);
    updateSearch(defaultSearch);
  };

  useEffect(() => {
    if (shouldOpenMobileSearch) {
      setIsMobileSearchVisible(true);
    }
  }, [shouldOpenMobileSearch]);

  if (isTabletPortraitAndSmaller && isMobileSearchVisible) {
    return (
      <ProductSearchMobile
        search={search}
        onUpdateSearch={updateSearch}
        onSearch={handleSearch}
        onClose={handleClose}
      />
    );
  }

  return (
    <Page
      Header={
        !isTabletPortraitAndSmaller ? (
          <Header
            Search={
              <Header.Search autoFocus={shouldFocus} value={search.q} onSearch={handleSearch} />
            }
          />
        ) : undefined
      }
    >
      {isTabletPortraitAndSmaller && (
        <Container>
          <div className={styles.searchbar}>
            <Header.SearchLink />
          </div>
        </Container>
      )}
      {isTabletPortraitAndSmaller || !showSearchResults ? (
        <BrowseCatalog />
      ) : (
        <ProductSearch search={search} onUpdateSearch={updateSearch} />
      )}
    </Page>
  );
};

export default ProductSearchPage;
